import axios from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Tab,
  Typography,
} from "@material-ui/core";
import { TabContext, TabList, TabPanel } from "@material-ui/lab";
import Alert from "@material-ui/lab/Alert";

import DataTable from "./DataTable";
import RefreshButton from "./RefreshButton";
import UptimeChart from "./UptimeChart";
import VpnPingChart from "./VpnPingChart";
import normalizeAxiosDetail from "../utils/normalizeAxiosDetail";

const TABS = {
  uptimePing: "uptime_ping",
  vpnPing: "vpn_ping",
  memory: "memory",
  configs: "configs",
  alerts: "alerts",
  logs: "logs",
  reports: "reports",
};

const memoryColumns = [
  { field: "timestamp", headerName: "Timestamp", flex: 1.6, minWidth: 220 },
  { field: "usedMb", headerName: "Used (MB)", flex: 1, minWidth: 120 },
  { field: "freeMb", headerName: "Free (MB)", flex: 1, minWidth: 120 },
  { field: "totalMb", headerName: "Total (MB)", flex: 1, minWidth: 120 },
  { field: "percentUsed", headerName: "Used %", flex: 0.9, minWidth: 100 },
  { field: "threshold", headerName: "Reset Threshold", flex: 1.2, minWidth: 140 },
];

const alertColumns = [
  { field: "timestamp", headerName: "Timestamp", flex: 1.5, minWidth: 210 },
  { field: "severity", headerName: "Severity", flex: 0.9, minWidth: 110 },
  { field: "reason", headerName: "Reason", flex: 2.2, minWidth: 260 },
  { field: "usedPercent", headerName: "Used %", flex: 0.9, minWidth: 100 },
];

const configColumns = [
  { field: "timestamp", headerName: "Timestamp", flex: 1.6, minWidth: 220 },
  { field: "hostname", headerName: "Hostname", flex: 1.3, minWidth: 170 },
  { field: "firmwareVersion", headerName: "Firmware", flex: 1.2, minWidth: 150 },
  { field: "backupVersion", headerName: "Backup Version", flex: 1.2, minWidth: 150 },
  { field: "ssidCount", headerName: "SSIDs", flex: 0.8, minWidth: 90 },
  { field: "interfaceCount", headerName: "Interfaces", flex: 1, minWidth: 110 },
];

const logsColumns = [
  { field: "timestamp", headerName: "Timestamp", flex: 1.5, minWidth: 210 },
  { field: "severity", headerName: "Severity", flex: 0.9, minWidth: 110 },
  { field: "alertReason", headerName: "Alert Reason", flex: 2.2, minWidth: 260 },
  { field: "kernelEntries", headerName: "Kernel Entries", flex: 1, minWidth: 130 },
  { field: "syslogEntries", headerName: "Syslog Entries", flex: 1, minWidth: 130 },
];

const processesColumns = [
  { field: "name", headerName: "Process", flex: 2, minWidth: 220 },
  { field: "memoryPercent", headerName: "Memory %", flex: 1, minWidth: 120 },
  { field: "rss", headerName: "RSS", flex: 1, minWidth: 110 },
  { field: "pid", headerName: "PID", flex: 0.8, minWidth: 90 },
];

const PAGINATION_DEBOUNCE_MS = 300;

export default function DeviceMonitor({ deviceUuid }) {
  const [tabValue, setTabValue] = useState(TABS.uptimePing);
  const [uptimeRefreshKey, setUptimeRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();

  const [memoryData, setMemoryData] = useState([]);
  const [memoryStats, setMemoryStats] = useState();
  const [memoryAlerts, setMemoryAlerts] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [latestConfig, setLatestConfig] = useState();
  const [logs, setLogs] = useState([]);
  const [logsStats, setLogsStats] = useState();
  const [deviceReport, setDeviceReport] = useState();
  const [restoreScriptLoading, setRestoreScriptLoading] = useState(false);

  const [memoryMeta, setMemoryMeta] = useState({ page: 1, page_size: 10, total: 0 });
  const [alertMeta, setAlertMeta] = useState({ page: 1, page_size: 10, total: 0 });
  const [configMeta, setConfigMeta] = useState({ page: 1, page_size: 10, total: 0 });
  const [logMeta, setLogMeta] = useState({ page: 1, page_size: 10, total: 0 });
  const [memoryQuery, setMemoryQuery] = useState({ page: 1, page_size: 10 });
  const [alertQuery, setAlertQuery] = useState({ page: 1, page_size: 10 });
  const [configQuery, setConfigQuery] = useState({ page: 1, page_size: 10 });
  const [logQuery, setLogQuery] = useState({ page: 1, page_size: 10 });

  const memoryRequestRef = useRef(0);
  const alertRequestRef = useRef(0);
  const configRequestRef = useRef(0);
  const logRequestRef = useRef(0);
  const reportsRequestRef = useRef(0);
  const skipMemoryStatsRef = useRef(false);
  const skipConfigLatestRef = useRef(false);
  const skipLogsStatsRef = useRef(false);

  const noopSort = () => {};

  const resetMonitorTabState = useCallback(() => {
    skipMemoryStatsRef.current = false;
    skipConfigLatestRef.current = false;
    skipLogsStatsRef.current = false;
    setMemoryStats(undefined);
    setLatestConfig(undefined);
    setLogsStats(undefined);
    setDeviceReport(undefined);
    setMemoryMeta({ page: 1, page_size: 10, total: 0 });
    setAlertMeta({ page: 1, page_size: 10, total: 0 });
    setConfigMeta({ page: 1, page_size: 10, total: 0 });
    setLogMeta({ page: 1, page_size: 10, total: 0 });
    setMemoryQuery({ page: 1, page_size: 10 });
    setAlertQuery({ page: 1, page_size: 10 });
    setConfigQuery({ page: 1, page_size: 10 });
    setLogQuery({ page: 1, page_size: 10 });
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setMemoryQuery({ page: memoryMeta.page, page_size: memoryMeta.page_size });
    }, PAGINATION_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [memoryMeta.page, memoryMeta.page_size]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAlertQuery({ page: alertMeta.page, page_size: alertMeta.page_size });
    }, PAGINATION_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [alertMeta.page, alertMeta.page_size]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setConfigQuery({ page: configMeta.page, page_size: configMeta.page_size });
    }, PAGINATION_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [configMeta.page, configMeta.page_size]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLogQuery({ page: logMeta.page, page_size: logMeta.page_size });
    }, PAGINATION_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [logMeta.page, logMeta.page_size]);

  useEffect(() => {
    resetMonitorTabState();
  }, [deviceUuid, resetMonitorTabState]);

  const refreshMonitorData = () => {
    setError(undefined);
    setTabValue(TABS.uptimePing);
    resetMonitorTabState();
    setUptimeRefreshKey((prev) => prev + 1);
  };

  const fetchMemoryTab = useCallback(() => {
    const requestId = ++memoryRequestRef.current;
    setLoading(true);
    setError(undefined);
    const offset = (memoryQuery.page - 1) * memoryQuery.page_size;
    const requests = [
      axios.get(
        `/device/${deviceUuid}/monitor/memory?limit=${memoryQuery.page_size}&offset=${offset}`
      ),
    ];
    const fetchStats = !skipMemoryStatsRef.current;
    if (fetchStats) {
      requests.push(axios.get(`/device/${deviceUuid}/monitor/memory_stats`));
    }
    Promise.allSettled(requests)
      .then(([memory, memoryStatsResponse]) => {
        if (requestId !== memoryRequestRef.current) return;
        if (memory.status === "fulfilled") {
          setMemoryData(memory.value?.data?.memory_records || []);
          setMemoryMeta((prev) => ({ ...prev, total: memory.value?.data?.total || 0 }));
        }
        if (memoryStatsResponse && memoryStatsResponse.status === "fulfilled") {
          setMemoryStats(memoryStatsResponse.value?.data);
        }
        if (memory.status === "rejected") {
          setError(
            normalizeAxiosDetail(
              memory.reason?.response?.data?.detail,
              memory.reason?.message
            )
          );
        } else if (
          fetchStats &&
          memoryStatsResponse &&
          memoryStatsResponse.status === "rejected"
        ) {
          setError(
            normalizeAxiosDetail(
              memoryStatsResponse.reason?.response?.data?.detail,
              memoryStatsResponse.reason?.message
            )
          );
        }
      })
      .finally(() => {
        if (requestId !== memoryRequestRef.current) return;
        if (fetchStats) {
          skipMemoryStatsRef.current = true;
        }
        setLoading(false);
      });
  }, [deviceUuid, memoryQuery.page, memoryQuery.page_size]);

  const fetchAlertTab = useCallback(() => {
    const requestId = ++alertRequestRef.current;
    setLoading(true);
    setError(undefined);
    const offset = (alertQuery.page - 1) * alertQuery.page_size;
    axios
      .get(
        `/device/${deviceUuid}/monitor/memory_alerts?limit=${alertQuery.page_size}&offset=${offset}`
      )
      .then((response) => {
        if (requestId !== alertRequestRef.current) return;
        setMemoryAlerts(response?.data?.alerts || []);
        setAlertMeta((prev) => ({ ...prev, total: response?.data?.total || 0 }));
      })
      .catch((requestError) => {
        if (requestId !== alertRequestRef.current) return;
        setError(normalizeAxiosDetail(requestError.response?.data?.detail, requestError.message));
      })
      .finally(() => {
        if (requestId !== alertRequestRef.current) return;
        setLoading(false);
      });
  }, [deviceUuid, alertQuery.page, alertQuery.page_size]);

  const fetchConfigTab = useCallback(() => {
    const requestId = ++configRequestRef.current;
    setLoading(true);
    setError(undefined);
    const offset = (configQuery.page - 1) * configQuery.page_size;
    const requests = [
      axios.get(
        `/device/${deviceUuid}/monitor/config_backups?limit=${configQuery.page_size}&offset=${offset}`
      ),
    ];
    const fetchLatest = !skipConfigLatestRef.current;
    if (fetchLatest) {
      requests.push(axios.get(`/device/${deviceUuid}/monitor/config_backups/latest`));
    }
    Promise.allSettled(requests)
      .then(([backups, latest]) => {
        if (requestId !== configRequestRef.current) return;
        if (backups.status === "fulfilled") {
          setConfigs(backups.value?.data?.backups || []);
          setConfigMeta((prev) => ({ ...prev, total: backups.value?.data?.total || 0 }));
        }
        if (latest && latest.status === "fulfilled") {
          setLatestConfig(latest.value?.data);
        }
        if (backups.status === "rejected") {
          setError(
            normalizeAxiosDetail(backups.reason?.response?.data?.detail, backups.reason?.message)
          );
        } else if (fetchLatest && latest && latest.status === "rejected") {
          setError(normalizeAxiosDetail(latest.reason?.response?.data?.detail, latest.reason?.message));
        }
      })
      .finally(() => {
        if (requestId !== configRequestRef.current) return;
        if (fetchLatest) {
          skipConfigLatestRef.current = true;
        }
        setLoading(false);
      });
  }, [deviceUuid, configQuery.page, configQuery.page_size]);

  const fetchLogsTab = useCallback(() => {
    const requestId = ++logRequestRef.current;
    setLoading(true);
    setError(undefined);
    const offset = (logQuery.page - 1) * logQuery.page_size;
    const requests = [
      axios.get(`/device/${deviceUuid}/monitor/logs?limit=${logQuery.page_size}&offset=${offset}`),
    ];
    const fetchStats = !skipLogsStatsRef.current;
    if (fetchStats) {
      requests.push(axios.get(`/device/${deviceUuid}/monitor/logs/stats`));
    }
    Promise.allSettled(requests)
      .then(([logsResponse, stats]) => {
        if (requestId !== logRequestRef.current) return;
        if (logsResponse.status === "fulfilled") {
          setLogs(logsResponse.value?.data?.records || []);
          setLogMeta((prev) => ({ ...prev, total: logsResponse.value?.data?.total || 0 }));
        }
        if (stats && stats.status === "fulfilled") {
          setLogsStats(stats.value?.data);
        }
        if (logsResponse.status === "rejected") {
          setError(
            normalizeAxiosDetail(
              logsResponse.reason?.response?.data?.detail,
              logsResponse.reason?.message
            )
          );
        } else if (fetchStats && stats && stats.status === "rejected") {
          setError(
            normalizeAxiosDetail(stats.reason?.response?.data?.detail, stats.reason?.message)
          );
        }
      })
      .finally(() => {
        if (requestId !== logRequestRef.current) return;
        if (fetchStats) {
          skipLogsStatsRef.current = true;
        }
        setLoading(false);
      });
  }, [deviceUuid, logQuery.page, logQuery.page_size]);

  const fetchReportsTab = useCallback(() => {
    const requestId = ++reportsRequestRef.current;
    setLoading(true);
    setError(undefined);
    axios
      .get(`/device/${deviceUuid}/monitor/reports/device`)
      .then((response) => {
        if (requestId !== reportsRequestRef.current) return;
        setDeviceReport(response?.data);
      })
      .catch((requestError) => {
        if (requestId !== reportsRequestRef.current) return;
        setError(normalizeAxiosDetail(requestError.response?.data?.detail, requestError.message));
      })
      .finally(() => {
        if (requestId !== reportsRequestRef.current) return;
        setLoading(false);
      });
  }, [deviceUuid]);

  useEffect(() => {
    if (!deviceUuid) return;
    if (tabValue === TABS.memory) {
      fetchMemoryTab();
      return;
    }
    if (tabValue === TABS.alerts) {
      fetchAlertTab();
      return;
    }
    if (tabValue === TABS.configs) {
      fetchConfigTab();
      return;
    }
    if (tabValue === TABS.logs) {
      fetchLogsTab();
      return;
    }
    if (tabValue === TABS.reports) {
      fetchReportsTab();
      return;
    }
    setError(undefined);
  }, [
    deviceUuid,
    tabValue,
    fetchMemoryTab,
    fetchAlertTab,
    fetchConfigTab,
    fetchLogsTab,
    fetchReportsTab,
  ]);

  const downloadRestoreScript = () => {
    setRestoreScriptLoading(true);
    axios
      .get(`/device/${deviceUuid}/monitor/config_backups/latest/restore_script`, {
        responseType: "text",
      })
      .then((response) => {
        const blob = new Blob([response.data], { type: "text/plain;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${latestConfig?.device_id || "device"}-restore.sh`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((requestError) => {
        setError(normalizeAxiosDetail(requestError.response?.data?.detail, requestError.message));
      })
      .finally(() => setRestoreScriptLoading(false));
  };

  const memoryRows = useMemo(
    () =>
      memoryData.map((record, index) => {
        const mem = record?.device_memory || {};
        return {
          id: record?.id || `${record?.timestamp || "memory"}-${index}`,
          timestamp: mem?.timestamp ? new Date(mem.timestamp).toLocaleString() : "-",
          usedMb: mem?.usedMemoryMb ?? "-",
          freeMb: mem?.freeMemoryMb ?? "-",
          totalMb: mem?.totalMemoryMb ?? "-",
          percentUsed: mem?.percentUsed ?? "-",
          threshold: mem?.resetThresholdPercent ?? "-",
        };
      }),
    [memoryData]
  );

  const processRows = useMemo(() => {
    const topProcesses = memoryStats?.top_processes || [];
    return topProcesses.map((process, index) => ({
      id: `${process?.pid || "process"}-${index}`,
      name: process?.name || "-",
      memoryPercent: process?.memoryPercent ?? process?.memory_percent ?? "-",
      rss: process?.rss ?? "-",
      pid: process?.pid ?? "-",
    }));
  }, [memoryStats]);

  const alertsRows = useMemo(
    () =>
      memoryAlerts.map((alert, index) => ({
        id: alert?.id || `${alert?.timestamp || "alert"}-${index}`,
        timestamp: alert?.timestamp ? new Date(alert.timestamp).toLocaleString() : "-",
        severity: alert?.severity || "-",
        reason: alert?.reason || alert?.message || "-",
        usedPercent: alert?.memoryPercent ?? alert?.memory_percent ?? "-",
      })),
    [memoryAlerts]
  );

  const configRows = useMemo(
    () =>
      configs.map((config, index) => ({
        id: config?.id || `config-${index}`,
        timestamp: config?.timestamp ? new Date(config.timestamp).toLocaleString() : "-",
        hostname: config?.hostname || "-",
        firmwareVersion: config?.firmware_version || "-",
        backupVersion: config?.backup_version || "-",
        ssidCount: config?.ssid_count ?? "-",
        interfaceCount: config?.interface_count ?? "-",
      })),
    [configs]
  );

  const logsRows = useMemo(
    () =>
      logs.map((record, index) => ({
        id: record?.id || `${record?.timestamp || "log"}-${index}`,
        timestamp: record?.timestamp ? new Date(record.timestamp).toLocaleString() : "-",
        severity: record?.severity || "-",
        alertReason: record?.alert_reason || "-",
        kernelEntries: record?.kernel_entries || 0,
        syslogEntries: record?.syslog_entries || 0,
      })),
    [logs]
  );

  return (
    <Grid container direction="column" spacing={3}>
      {error ? (
        <Grid item>
          <Alert severity="warning">{error}</Alert>
        </Grid>
      ) : null}

      <Grid item>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">Monitor</Typography>
          <RefreshButton refreshAction={refreshMonitorData} />
        </Box>
        <TabContext value={tabValue}>
          <AppBar position="static">
            <TabList
              onChange={(_, value) => setTabValue(value)}
              aria-label="monitor tabs"
              variant="scrollable"
              scrollButtons="auto"
              textColor="primary"
              indicatorColor="primary"
              style={{
                minHeight: 40,
                backgroundColor: "#f5f7fb",
                borderBottom: "1px solid #d9dfec",
              }}
            >
              <Tab
                label="Uptime Ping"
                value={TABS.uptimePing}
                style={{ minHeight: 40, textTransform: "none", color: "#374151", fontWeight: 600 }}
              />
              <Tab
                label="VPN Ping"
                value={TABS.vpnPing}
                style={{ minHeight: 40, textTransform: "none", color: "#374151", fontWeight: 600 }}
              />
              <Tab
                label="Memory"
                value={TABS.memory}
                style={{ minHeight: 40, textTransform: "none", color: "#374151", fontWeight: 600 }}
              />
              <Tab
                label="Configs"
                value={TABS.configs}
                style={{ minHeight: 40, textTransform: "none", color: "#374151", fontWeight: 600 }}
              />
              <Tab
                label="Memory Alerts"
                value={TABS.alerts}
                style={{ minHeight: 40, textTransform: "none", color: "#374151", fontWeight: 600 }}
              />
              <Tab
                label="Logs"
                value={TABS.logs}
                style={{ minHeight: 40, textTransform: "none", color: "#374151", fontWeight: 600 }}
              />
              <Tab
                label="Reports"
                value={TABS.reports}
                style={{ minHeight: 40, textTransform: "none", color: "#374151", fontWeight: 600 }}
              />
            </TabList>
          </AppBar>

          <TabPanel value={TABS.uptimePing}>
            {tabValue === TABS.uptimePing ? (
              <UptimeChart key={uptimeRefreshKey} deviceUuid={deviceUuid} />
            ) : null}
          </TabPanel>

          <TabPanel value={TABS.vpnPing}>
            {tabValue === TABS.vpnPing ? (
              <VpnPingChart key={uptimeRefreshKey} deviceUuid={deviceUuid} />
            ) : null}
          </TabPanel>

          <TabPanel value={TABS.memory}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2">Average Used %</Typography>
                    <Typography variant="h6">{memoryStats?.avg_memory_percent ?? "-"}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2">Peak Used %</Typography>
                    <Typography variant="h6">{memoryStats?.peak_memory_percent ?? "-"}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2">Critical Alerts</Typography>
                    <Typography variant="h6">{memoryStats?.critical_alert_count ?? 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <DataTable
                  data={memoryRows}
                  columns={memoryColumns}
                  loading={loading}
                  metaData={memoryMeta}
                  handleSort={noopSort}
                  handlePageChange={(page) =>
                    setMemoryMeta((prev) => ({ ...prev, page }))
                  }
                  handlePageSizeChange={(pageSize) =>
                    setMemoryMeta((prev) => ({ ...prev, page: 1, page_size: pageSize }))
                  }
                  disableSelectionOnClick
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Top Memory Processes</Typography>
                <DataTable
                  data={processRows}
                  columns={processesColumns}
                  loading={loading}
                  paginationMode="client"
                  initialState={{ pagination: { pageSize: 10 } }}
                  disableSelectionOnClick
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={TABS.configs}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2">
                Latest Backup:{" "}
                {latestConfig?.timestamp
                  ? new Date(latestConfig.timestamp).toLocaleString()
                  : "-"}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={downloadRestoreScript}
                disabled={restoreScriptLoading}
              >
                {restoreScriptLoading ? "Downloading..." : "Download Restore Script"}
              </Button>
            </Box>
            <DataTable
              data={configRows}
              columns={configColumns}
              loading={loading}
              metaData={configMeta}
              handleSort={noopSort}
              handlePageChange={(page) => setConfigMeta((prev) => ({ ...prev, page }))}
              handlePageSizeChange={(pageSize) =>
                setConfigMeta((prev) => ({ ...prev, page: 1, page_size: pageSize }))
              }
              disableSelectionOnClick
            />
          </TabPanel>

          <TabPanel value={TABS.alerts}>
            <DataTable
              data={alertsRows}
              columns={alertColumns}
              loading={loading}
              metaData={alertMeta}
              handleSort={noopSort}
              handlePageChange={(page) => setAlertMeta((prev) => ({ ...prev, page }))}
              handlePageSizeChange={(pageSize) =>
                setAlertMeta((prev) => ({ ...prev, page: 1, page_size: pageSize }))
              }
              disableSelectionOnClick
            />
          </TabPanel>

          <TabPanel value={TABS.logs}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2">Total Log Records</Typography>
                    <Typography variant="h6">{logsStats?.total_log_records ?? "-"}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2">Comprehensive Alerts</Typography>
                    <Typography variant="h6">{logsStats?.comprehensive_alerts ?? "-"}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <DataTable
                  data={logsRows}
                  columns={logsColumns}
                  loading={loading}
                  metaData={logMeta}
                  handleSort={noopSort}
                  handlePageChange={(page) => setLogMeta((prev) => ({ ...prev, page }))}
                  handlePageSizeChange={(pageSize) =>
                    setLogMeta((prev) => ({ ...prev, page: 1, page_size: pageSize }))
                  }
                  disableSelectionOnClick
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={TABS.reports}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2">Restart Count</Typography>
                    <Typography variant="h6">{deviceReport?.restart_count ?? "-"}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2">Sample Count</Typography>
                    <Typography variant="h6">{deviceReport?.sample_count ?? "-"}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2">Stable Device</Typography>
                    <Typography variant="h6">
                      {deviceReport?.is_stable_device === true
                        ? "Yes"
                        : deviceReport?.is_stable_device === false
                        ? "No"
                        : "-"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2">Average Uptime (s)</Typography>
                    <Typography variant="h6">{deviceReport?.avg_uptime_seconds ?? "-"}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2">Max Uptime (s)</Typography>
                    <Typography variant="h6">{deviceReport?.max_uptime_seconds ?? "-"}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </TabContext>
      </Grid>
    </Grid>
  );
}
